import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  TextField,
  Alert,
  IconButton,
  Button,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import "../../../assets/css/time.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  FormWrapper,
  EntryWrapper,
  EntryTitle,
  EntryField,
} from "../../../components/customStyles/form"
import ICompany, { defaultCompany, IEditCompanyRequest } from "../../../models/company";
import CompanyService from "../../../services/company/CompanyService";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditOnly from "../../../components/permissions/EditOnly";

const CompanyEditPage = () => {
  const { t } = useTranslation(["common", "admin"]);
  const navigate = useNavigate();

  const [company, setCompany] = useState<ICompany>(defaultCompany);
  const [cachedCompany, setCachedCompany] = useState<ICompany>(defaultCompany);
  const [isCompanyChanged, setIsCompanyChanged] = useState(false);
  const [isErrorMessageVisible, setIsErrorMessageVisible] = useState(false);

  useEffect(() => {
    const getCompany = async () => {
      try {
        const companyData = await CompanyService.GetCompanyByUser();
        setCompany(companyData);
        setCachedCompany(companyData);
      } catch (error) {
      }
    };

    getCompany();
  }, []);

  const updateCompany = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsErrorMessageVisible(false);

    try {
      const editCompanyRequest: IEditCompanyRequest = {
        id: company.id,
        name: company.name,
        insideScheduleTolerance: company.insideScheduleTolerance,
        outsideScheduleTolerance: company.outsideScheduleTolerance,
      };

      await CompanyService.PutCompany(editCompanyRequest);
      navigate(0);

    } catch (error) {
      setIsErrorMessageVisible(true);
    }
  };

  useEffect(() => {
    setIsCompanyChanged(JSON.stringify(cachedCompany) != JSON.stringify(company));
  }, [company, cachedCompany]);

  const updateCompanyState = <T extends keyof IEditCompanyRequest>(
    field: T,
    value: IEditCompanyRequest[T]
  ) => {
    setCompany((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const isCompanyValid = () => {
    if (!company.name) return false;
    if (company.outsideScheduleTolerance < 0 || company.outsideScheduleTolerance > 60) return false;
    if (company.insideScheduleTolerance < 0 || company.insideScheduleTolerance > 60) return false;

    return true;
  };

  return (
    <Box component={Paper} sx={{ width: "100%" }}>
      <form autoComplete="off" onSubmit={updateCompany}>
        <FormWrapper>
          {isErrorMessageVisible && (
            <Alert
              severity="error"
              sx={{ marginBottom: "1em" }}
            >
              {t("table.company.editErrorMessage")}
            </Alert>
          )}

          <EntryWrapper>
            <EntryTitle>
              {t("table.company.code")}
              <Tooltip
                title={t("table.company.codeTooltip")}
                sx={{ marginLeft: "0.5em" }}
              >
                <InfoOutlinedIcon fontSize="small"></InfoOutlinedIcon>
              </Tooltip>
            </EntryTitle>
            <EntryField>
              <TextField
                required={true}
                disabled={true}
                style={{ textAlign: "left", width: "100%" }}
                value={company.code}
              />
            </EntryField>
          </EntryWrapper>

          <EntryWrapper>
            <EntryTitle>
              {t("table.company.name")} *
            </EntryTitle>

            <EntryField>
              <TextField
                required={true}
                style={{ textAlign: "left", width: "100%" }}
                value={company.name}
                onChange={(e) => {
                  updateCompanyState(
                    "name",
                    e.target.value
                  );
                }}
              />
            </EntryField>
          </EntryWrapper>

          <Box sx={{ borderLeft: '3px solid', borderColor: 'primary.main', pl: 2, mt: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body1" fontWeight="bold">{t("table.company.scheduleTolerance")}</Typography>

            <EntryWrapper>
              <EntryTitle sx={{ alignItems: 'center' }}>
                {t("table.company.outsideScheduleTolerance")}
                <Tooltip
                  title={t("table.company.outsideScheduleToleranceTooltip")}
                  sx={{ marginX: 2 }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </Tooltip>
              </EntryTitle>
              <EntryField>
                <TextField
                  type="number"
                  inputProps={{ min: 0, max: 60, step: 1 }}
                  style={{ textAlign: "left", width: "100%" }}
                  value={company.outsideScheduleTolerance}
                  onChange={(e) =>
                    updateCompanyState(
                      "outsideScheduleTolerance",
                      Math.floor(Number(e.target.value))
                    )
                  }
                  placeholder="Minutes (e.g., 5)"
                />
              </EntryField>
            </EntryWrapper>

            <EntryWrapper>
              <EntryTitle sx={{ alignItems: 'center' }}>
                {t("table.company.insideScheduleTolerance")}
                <Tooltip
                  title={t("table.company.insideScheduleToleranceTooltip")}
                  sx={{ marginX: 2 }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </Tooltip>
              </EntryTitle>
              <EntryField>
                <TextField
                  type="number"
                  inputProps={{ min: 0, max: 60, step: 1 }}
                  style={{ textAlign: "left", width: "100%" }}
                  value={company.insideScheduleTolerance}
                  onChange={(e) =>
                    updateCompanyState(
                      "insideScheduleTolerance",
                      Math.floor(Number(e.target.value))
                    )
                  }
                  placeholder="Minutes (e.g., 5)"
                />
              </EntryField>
            </EntryWrapper>
          </Box>

          <EntryWrapper
            sx={{ justifyContent: "flex-end", marginY: "1em" }}
          >
            <EditOnly>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                disabled={!isCompanyChanged || !isCompanyValid()}
                sx={{
                  marginLeft: "1em",
                }}
              >
                {t("table.company.edit")}
              </Button>
            </EditOnly>

          </EntryWrapper>
        </FormWrapper >
      </form >
    </Box >
  );
};

export default CompanyEditPage;
